import { format as d3Format } from 'd3-format';

export const getNumberAbbrev = (amount: number, decimalPlace: number = 0) => {
  if (amount >= 1e9) {
    return `${(amount / 1e9).toFixed(decimalPlace)}B`;
  }

  if (amount >= 1e6) {
    return `${(amount / 1e6).toFixed(decimalPlace)}M`;
  }

  if (amount >= 1e4) {
    return `${(amount / 1e3).toFixed(decimalPlace)}K`;
  }

  return amount.toFixed(decimalPlace);
};

// gets formatted number abbreviation based on numeric scale used in the country
// Reference: https://www.smartick.com/blog/other-contents/curiosities/european-billion-american-billion/#:~:text=In%20summary%2C%20it's%20important%20to,1%20000%20000%20000%20000
export const getFormattedNumberAbbrev = (
  value: number,
  countryCode = '',
  decimalPlace = 0,
  minDigitsToAbbreviate = 4
) => {
  if (Math.floor(Math.abs(value)).toString().length < minDigitsToAbbreviate) {
    // for values less than a 1000, decimal places should be hidden
    return value.toFixed(minDigitsToAbbreviate === 4 ? 0 : minDigitsToAbbreviate);
  }

  // Number Abbrev - Europe
  const abbrevEu: string[] = [
    '',
    'K',
    'M',
    'K M',
    'B',
    'K B',
    'T',
  ];

  // Number Abbrev - US
  const abbrevUs: string[] = [
    '',
    'K',
    'M',
    'B',
    'T',
    'q',
    'Q',
  ];

  // Country Codes - Using Number Abbrev - Europe
  const countryCodesEu: string[] = [
    'br',
    'pe',
    'sp',
    'co',
    'es',
    'lt',
    'cl',
    'ec',
    'mx',
    'gt',
  ];

  const selectedAbbrev = countryCode
    ? (countryCodesEu.includes(countryCode)
      ? abbrevEu
      : abbrevUs)
    : abbrevUs;

  const isValuePositive = value >= 0;
  const absValue = Math.abs(value);
  const thousandDigits = 3;

  for (let i = 0; i < selectedAbbrev.length; i++) {
    if (absValue < Math.pow(10, (i * thousandDigits))) {
      if (i === 0) {
        return value.toFixed(decimalPlace).toString();
      }

      const prevIndex = i - 1;
      const trimmedValue = absValue / Math.pow(10, (prevIndex * thousandDigits));
      const finalValue = (isValuePositive ? 1 : -1)
        * Math.round(trimmedValue * Math.pow(10, decimalPlace)) / Math.pow(10, decimalPlace);
      return `${finalValue.toFixed(decimalPlace)}${selectedAbbrev[prevIndex]}`;
    }
  }

  return value.toString();
}

/**
 * Handles formatting for larger numbers, with comma separation
 * @example
 * // returns "1,000"
 * formatNumber(1000);
 * @returns {string} Returns the formatted value.
 */
export const formatNumber = (value: number): string => {
  return `${value < 0 ? '-' : ''}${d3Format(',')(Math.abs(value))}`
};
